import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink, Loader2 } from "lucide-react";

export default function PublicBioPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: page, isLoading } = useQuery({
    queryKey: ["public-bio", slug],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("link_in_bio_pages")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();
      return data as any | null;
    },
  });

  const { data: items = [] } = useQuery({
    queryKey: ["public-bio-items", page?.id],
    enabled: !!page?.id,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("link_in_bio_items")
        .select("*")
        .eq("page_id", page!.id)
        .eq("is_active", true)
        .order("position");
      return (data ?? []) as any[];
    },
  });

  const trackClick = async (itemId: string) => {
    await (supabase as any)
      .from("link_in_bio_items")
      .update({ click_count: (items.find((i: any) => i.id === itemId)?.click_count ?? 0) + 1 })
      .eq("id", itemId);
  };

  // Track page view once
  useQuery({
    queryKey: ["bio-pageview", page?.id],
    enabled: !!page?.id,
    queryFn: async () => {
      await (supabase as any)
        .from("link_in_bio_pages")
        .update({ view_count: (page?.view_count ?? 0) + 1 })
        .eq("id", page!.id);
      return null;
    },
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-300 mb-2">404</p>
          <p className="text-gray-500">This page doesn't exist or hasn't been published yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4"
      style={{ background: page.bg_color ?? "#0D2240" }}
    >
      <div className="w-full max-w-sm mx-auto flex flex-col items-center gap-4">
        {/* Avatar */}
        {page.avatar_url ? (
          <img
            src={page.avatar_url}
            alt={page.title}
            className="w-20 h-20 rounded-full object-cover border-2 border-white/20"
          />
        ) : (
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold"
            style={{ background: "rgba(255,255,255,0.15)", color: page.text_color ?? "#fff" }}
          >
            {(page.title ?? "?").charAt(0).toUpperCase()}
          </div>
        )}

        {/* Title */}
        <h1
          className="text-xl font-bold text-center"
          style={{ color: page.text_color ?? "#fff" }}
        >
          {page.title}
        </h1>

        {/* Bio */}
        {page.bio && (
          <p
            className="text-sm text-center opacity-75 max-w-xs"
            style={{ color: page.text_color ?? "#fff" }}
          >
            {page.bio}
          </p>
        )}

        {/* Links */}
        <div className="w-full space-y-3 mt-2">
          {items.map((item: any) => (
            <a
              key={item.id}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick(item.id)}
              className="flex items-center gap-3 w-full py-3 px-5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 active:scale-95"
              style={{ background: page.button_color ?? "#006B3C" }}
            >
              <span className="text-lg shrink-0">{item.icon}</span>
              <span className="flex-1 text-center">{item.title}</span>
              <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-50" />
            </a>
          ))}
        </div>

        {/* Footer */}
        <p className="text-[10px] mt-6 opacity-30" style={{ color: page.text_color ?? "#fff" }}>
          Powered by MuseMind
        </p>
      </div>
    </div>
  );
}
